import React from 'react';
import styled from 'styled-components';

import { Box } from 'design/atoms/Box';

/*
Component's API:
<Accordion>
  ...
  <AccordianItem>
    <AccordionHeader></AccordionHeader>
    <AccordianBody></AccordianBody>
  </AccordianItem>
  ...
</Accordion>
*/

const FlexColumn = styled.div<{ mt?: string }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: ${props => (props.mt ? props.mt : '0px')};
`;

const Accordion = ({ children }) => {
  const [activeItems, setActiveItems] = React.useState<number[]>([]);

  const isItemActive = React.useCallback(idx => activeItems.includes(idx), [
    activeItems,
  ]);

  const handleItemClick = React.useCallback(
    idx => {
      let currentActiveItems = [...activeItems];

      if (isItemActive(idx)) {
        currentActiveItems = currentActiveItems.filter(x => x !== idx);
      } else {
        currentActiveItems.push(idx);
      }

      setActiveItems(currentActiveItems);
    },
    [activeItems, setActiveItems, isItemActive]
  );

  return (
    <FlexColumn>
      {React.Children.map(children, (child, idx) =>
        React.cloneElement(child, {
          idx,
          isActive: isItemActive(idx),
          onClick: handleItemClick,
        })
      )}
    </FlexColumn>
  );
};

type AccordionItemChildrenType = ({
  isActive,
  toggle,
}: {
  isActive: boolean;
  toggle: () => void;
}) => React.ReactNode;

type AccordionItemType = {
  isActive?: boolean;
  onClick?: (idx?: number) => void;
  idx?: number;
  children: AccordionItemChildrenType;
  mt?: string;
  pt?: string;
};

type AccordionHeaderProps = {
  children: React.ReactNode;
  bg?: string;
  onClick?: Function;
};

type AccordionBodyProps = {
  children: React.ReactNode;
  bg?: string;
};

export const AccordionItem: React.FC<AccordionItemType> = ({
  children,
  isActive,
  onClick,
  idx,
  mt,
  pt,
}) => {
  return (
    <FlexColumn mt={mt}>
      {children({
        isActive: isActive || false,
        toggle: () => onClick && onClick(idx),
      })}
    </FlexColumn>
  );
};

export const AccordionHeader: React.FC<AccordionHeaderProps> = ({
  bg,
  children,
  onClick,
}) => {
  return (
    <Box
      borderRadius={1}
      min-height="70px"
      px={3}
      pt="11px"
      width="100%"
      bg={bg ? bg : 'white'}
      color="white"
      height={70}
      onClick={onClick && onClick}
    >
      {children}
    </Box>
  );
};

export const AccordionBody: React.FC<AccordionBodyProps> = ({
  bg,
  children,
}) => {
  return (
    <Box width="100%" p={3} bg={bg ? bg : 'disabled'}>
      {children}
    </Box>
  );
};

Accordion.AccordionItem = AccordionItem;
Accordion.AccordionHeader = AccordionHeader;
Accordion.AccordionBody = AccordionBody;

export default Accordion;
